import type { BundlePdpResponse } from "@halm-club/halm-shared";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { PdpBuyBox } from "./sections/PdpBuyBox";
import { ProductInformation } from "../../../shared/pdp/components/sections/ProductInformation";
import { ObjectionReasons } from "../../../shared/pdp/components/sections/ObjectionReasons";
import { ReviewSection } from "../../../shared/pdp/components/sections/ReviewSection";
import {
  getBundlePlaceholderValues,
  replaceInformationItemPlaceholders,
} from "../../../shared/pdp/utils/placeholders";
import "./BundlePdpLayout.css";

type BundlePdpLayoutProps = {
  bundle: BundlePdpResponse;
  compareAtPrice: number;
  discountPercentage: number;
  isAddingToCart: boolean;
  isSkipVisible: boolean;
  objectionReasonsPortalTarget: HTMLElement | null;
  onAddToCart: () => void;
  onSkip: () => void;
  onVariantChange: (variantId: string) => void;
  price: number;
  selectedVariantId: string;
  statusMessage: string;
};

const getRandomBuyerCount = () =>
  Math.floor(Math.random() * (1600 - 1400 + 1)) + 1400;

export function BundlePdpLayout({
  bundle,
  compareAtPrice,
  discountPercentage,
  isAddingToCart,
  isSkipVisible,
  objectionReasonsPortalTarget,
  onAddToCart,
  onSkip,
  onVariantChange,
  price,
  selectedVariantId,
  statusMessage,
}: BundlePdpLayoutProps) {
  const [buyerCount] = useState(getRandomBuyerCount);
  const currentNavigationStep = bundle.navigationSteps?.find(
    (step) => step.isCurrentPage,
  );
  const selectedVariant =
    bundle.variants.find(
      (variant) => variant.variantId === selectedVariantId,
    ) ?? bundle.variants[0];
  const informationItems = useMemo(
    () =>
      replaceInformationItemPlaceholders(
        bundle.collapsibleInformation,
        getBundlePlaceholderValues(selectedVariant?.onetimeProductVariants),
      ),
    [bundle.collapsibleInformation, selectedVariant?.onetimeProductVariants],
  );
  const objectionReasons = (
    <ObjectionReasons objectionReasons={bundle.objectionReasons} />
  );

  return (
    <div className="react-pdp__onetime-page">
      <div className="react-pdp__onetime-panel">
        <PdpBuyBox
          buttonSubText={currentNavigationStep?.navigationDescription}
          imageSources={bundle.imageSources}
          compareAtPrice={compareAtPrice}
          discountPercentage={discountPercentage}
          heading={bundle.heading}
          includedProducts={bundle.onetimeSingleProducts}
          informationItems={informationItems}
          buyerCount={buyerCount}
          isAddingToCart={isAddingToCart}
          isSkipVisible={isSkipVisible}
          onAddToCart={onAddToCart}
          onSkip={onSkip}
          onVariantChange={onVariantChange}
          price={price}
          productId={bundle.productId}
          reviewContent={bundle.reviewContent}
          selectedVariantId={selectedVariantId}
          statusMessage={statusMessage}
          subheading={bundle.subheading}
          tags={bundle.tags}
          title={bundle.title}
          titleTag={bundle.titleTag}
          variants={bundle.variants}
        />

        <ProductInformation
          title={bundle.title}
          informationItems={informationItems}
        />
        <ReviewSection
          buyerCount={buyerCount}
          reviewContent={bundle.reviewContent}
        />
        {objectionReasonsPortalTarget
          ? createPortal(objectionReasons, objectionReasonsPortalTarget)
          : objectionReasons}
      </div>
    </div>
  );
}
